import { useLogger } from '@booksy-com/vue-tools'

import { getExperimentWebBasedSubscriptionEnabled, WEB_BASED_SUBSCRIPTION_ALLOCATIONS } from '@/api'
import { useCore } from '@/composables/core'
import flags from '@/lib/flags'
import { LOCAL_STORAGE_KEYS, BUSINESS_STATUSES } from '@/lib/consts.ts'
import {
  SUBSCRIPTION_OFFER_ENTRY_SOURCE_QUERY_KEY,
  SUBSCRIPTION_OFFER_ENTRY_SOURCES,
} from '@/components/settings/subscription/consts'

import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'

const logger = useLogger('useExperimentWebBasedSubscriptionRedirect')

export type UseExperimentWebBasedSubscriptionRedirectReturn = {
  shouldRedirectToYourSubscription: () => Promise<boolean>
}

export function useExperimentWebBasedSubscriptionRedirect (): UseExperimentWebBasedSubscriptionRedirectReturn {
  const { store } = useCore()
  const businessId = store.getters['business/businessId'] as string | number

  const isManagerOrOwner = store.getters['identity/isManagerOrHigher'] as boolean
  const country = store.getters['app/country'] as string
  const businessStatus = store.getters['business/businessStatus'] as string | undefined

  const shouldRedirectToYourSubscription = async (): Promise<boolean> => {
    if (!isManagerOrOwner) {
      return false
    }

    if (country !== 'us') {
      return false
    }

    if (businessStatus !== BUSINESS_STATUSES.TRIAL) {
      return false
    }

    if (flags.getFlag(LOCAL_STORAGE_KEYS.EXPERIMENT_WEB_BASED_SUBSCRIPTION, false, String(businessId))) {
      return false
    }

    try {
      const { data } = await getExperimentWebBasedSubscriptionEnabled(businessId)

      if (data?.allocation === WEB_BASED_SUBSCRIPTION_ALLOCATIONS.VARIANT_A) {
        flags.setFlag(
          LOCAL_STORAGE_KEYS.EXPERIMENT_WEB_BASED_SUBSCRIPTION,
          true,
          String(businessId)
        )
        return true
      }
    } catch (error) {
      logger.error('Failed to get web-based subscription experiment:', error)
    }

    return false
  }

  return { shouldRedirectToYourSubscription }
}

export async function experimentWebBasedSubscriptionBeforeEnter (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> {
  const { shouldRedirectToYourSubscription } = useExperimentWebBasedSubscriptionRedirect()

  if (await shouldRedirectToYourSubscription()) {
    next({
      name: 'settings.subscription.your-subscription',
      params: { businessId: to.params.businessId },
      query: {
        [SUBSCRIPTION_OFFER_ENTRY_SOURCE_QUERY_KEY]: SUBSCRIPTION_OFFER_ENTRY_SOURCES.PLAN_INFO_MODAL,
      },
    })
    return
  }

  next()
}
